/**
 * Thunderstorms. Strikes fall in heavy rain only, at random but repeatably: time is cut into slots, and each slot's roll (from
 * the slot number and the map seed) decides whether it holds a strike, when, where and how hard. Everything here is a pure
 * function of time and rain, so the flash, the bolt and the thunder that follows all agree, and can be tested.
 */
export interface Strike {
  /** Seconds at which the bolt lands. */
  start: number;
  /** 0 overhead, 1 on the horizon: how far off the strike is, which sets how long the thunder takes to arrive and how loud it is. */
  distance: number;
  /** Where it lands, as a bearing in radians from the camera's focus. */
  angle: number;
  /** 0.5–1: how bright the flash is. */
  power: number;
  /** Seeds the bolt's jagged shape. */
  seed: number;
}

export const SLOT = 9;
/** Rain heavier than this brings thunder. */
export const STORM_RAIN = .6;

function hash(a: number, b: number, c: number) {
  let h = Math.imul(a + 7919, 0x9e3779b1) ^ Math.imul(b + 104729, 0x85ebca6b) ^ Math.imul(c | 0, 0xc2b2ae35);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d); h = Math.imul(h ^ (h >>> 12), 0x297a2d39); h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}
const smooth = (value: number, from: number, to: number) => { const t = Math.min(1, Math.max(0, (value - from) / (to - from))); return t * t * (3 - 2 * t); };

/** The strike in a time slot, if the rain is heavy enough to bring one: roughly one in two slots in a downpour. */
export function strikeInSlot(slot: number, rain: number, seed: number): Strike | null {
  if (rain < STORM_RAIN || hash(slot, 1, seed) > smooth(rain, STORM_RAIN, .95) * .55) return null;
  return { start: (slot + hash(slot, 2, seed) * .7) * SLOT, distance: .1 + hash(slot, 3, seed) * .9, angle: hash(slot, 4, seed) * Math.PI * 2, power: .5 + hash(slot, 5, seed) * .5, seed: Math.floor(hash(slot, 6, seed) * 1e6) };
}

/** Strikes whose bolt, or whose thunder, can fall in `[from, to]`. */
export function strikesBetween(from: number, to: number, rain: number, seed: number): Strike[] {
  const strikes: Strike[] = [];
  for (let slot = Math.floor((from - 9) / SLOT); slot <= Math.floor(to / SLOT); slot++) { const strike = strikeInSlot(slot, rain, seed); if (strike) strikes.push(strike); }
  return strikes;
}

/** The flash of one strike `seconds` after it began: a hard first stroke and two weaker returns. */
export function flashOf(strike: Strike, seconds: number): number {
  const t = seconds - strike.start, pulse = (at: number) => (t < at ? 0 : Math.exp(-(t - at) * 13));
  return Math.min(1, Math.max(pulse(0), .65 * pulse(.19), .4 * pulse(.37)) * strike.power * 1.15);
}

/** Total flash at `seconds`, 0–1. */
export function flashAt(seconds: number, rain: number, seed: number): number {
  let flash = 0;
  for (const strike of strikesBetween(seconds - 1, seconds, rain, seed)) flash = Math.max(flash, flashOf(strike, seconds));
  return flash;
}

/** Seconds from the flash to the thunder: the sound of a strike a few kilometres off. */
export const thunderDelay = (strike: Strike) => .6 + strike.distance * 5.5;
/** How loud the thunder is, 0–1. */
export const thunderStrength = (strike: Strike) => (1 - strike.distance * .65) * (.6 + strike.power * .4);

/** Thunder that sounds in `(from, to]`: each strike's delayed rumble, once. */
export function thunderBetween(from: number, to: number, rain: number, seed: number): { at: number; strength: number; distance: number }[] {
  return strikesBetween(from - 7, to, rain, seed).map(strike => ({ at: strike.start + thunderDelay(strike), strength: thunderStrength(strike), distance: strike.distance }))
    .filter(thunder => thunder.at > from && thunder.at <= to);
}

/** A jagged bolt from `height` metres down to the ground as line segments (pairs of points), with a few forks. Repeatable from the strike's seed. */
export function boltSegments(seed: number, height = 80): number[] {
  const out: number[] = [], rng = (n: number) => hash(seed, n, 99) - .5;
  const walk = (x: number, y: number, z: number, steps: number, spread: number, depth: number, salt: number) => {
    for (let i = 0; i < steps; i++) {
      const nx = x + rng(salt + i * 3) * spread, ny = y - height / 14 * (depth ? 1.5 : 1) * (.7 + hash(seed, salt + i * 3 + 1, 5) * .6), nz = z + rng(salt + i * 3 + 2) * spread;
      out.push(x, y, z, nx, Math.max(0, ny), nz);
      if (depth === 0 && i > 2 && i < steps - 2 && hash(seed, salt + i, 7) < .3) walk(nx, ny, nz, 4, spread * 1.5, 1, salt + 40 + i * 11);
      x = nx; y = ny; z = nz; if (y <= 0) break;
    }
  };
  walk(0, height, 0, 16, 7, 0, 1);
  // Whatever the steps left it, the main stroke always reaches the ground.
  const [x, y, z] = out.slice(out.length - 3);
  if (y > 0) out.push(x, y, z, x, 0, z);
  return out;
}
